import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface PageView {
  url: string;
  title: string;
  views: string | number;
}

const DetailedAnalyticsPage: React.FC = () => {
  useDocumentTitle('Detailed Analytics');
  const navigate = useNavigate();
  const { currentAsset } = useAuth();
  const [pageViews, setPageViews] = useState<PageView[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (currentAsset) {
      fetchPageViews();
    }
  }, [currentAsset, period]);

  const fetchPageViews = async () => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await analyticsAPI.getTopPages(currentAsset.id, period);
      setPageViews(response.data.data.topPages || []);
      setCurrentPage(1);
    } catch (error: any) {
      console.error('Failed to fetch page views:', error);
      toast.error('Failed to load page views data');
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (pageUrl: string) => {
    // Navigate to the full page detail view
    navigate(`/analytics/page/${encodeURIComponent(pageUrl)}?period=${period}`);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const filteredPages = pageViews.filter(page =>
    page.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (page.title && page.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredPages.length / PAGE_SIZE);
  const pagedItems = filteredPages.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const totalViews = pageViews.reduce((sum, page) => sum + Number(page.views), 0);
  const maxViews = pageViews.length > 0 ? Math.max(...pageViews.map(p => Number(p.views))) : 1;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading analytics...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Page Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h2 className="h3 mb-1">Detailed Analytics</h2>
                <p className="text-muted mb-0">Click on any page to see comprehensive analytics with charts</p>
              </div>
              <div className="d-flex gap-2">
                <div className="btn-group">
                  <button
                    className={`btn btn-sm ${period === '24h' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPeriod('24h')}
                  >
                    24h
                  </button>
                  <button
                    className={`btn btn-sm ${period === '7d' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPeriod('7d')}
                  >
                    7d
                  </button>
                  <button
                    className={`btn btn-sm ${period === '30d' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPeriod('30d')}
                  >
                    30d
                  </button>
                  <button
                    className={`btn btn-sm ${period === '90d' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPeriod('90d')}
                  >
                    90d
                  </button>
                  <button
                    className={`btn btn-sm ${period === '1y' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPeriod('1y')}
                  >
                    1y
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-primary bg-opacity-10 text-primary rounded me-3">
                    <i className="bi bi-file-earmark-text fs-3"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-1">Total Pages</p>
                    <h3 className="h4 mb-0">{formatNumber(pageViews.length)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-success bg-opacity-10 text-success rounded me-3">
                    <i className="bi bi-eye fs-3"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-1">Total Views</p>
                    <h3 className="h4 mb-0">{formatNumber(totalViews)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-info bg-opacity-10 text-info rounded me-3">
                    <i className="bi bi-graph-up fs-3"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-1">Avg Views/Page</p>
                    <h3 className="h4 mb-0">
                      {pageViews.length > 0 ? formatNumber(Math.round(totalViews / pageViews.length)) : 0}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-warning bg-opacity-10 text-warning rounded me-3">
                    <i className="bi bi-star fs-3"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-1">Top Page Views</p>
                    <h3 className="h4 mb-0">
                      {pageViews.length > 0 ? formatNumber(Number(pageViews[0]?.views || 0)) : 0}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search pages by URL or title..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page Views Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">All Pages</h5>
                <small className="text-muted">
                  {filteredPages.length > 0 && `${filteredPages.length} pages · `}
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  Click a row to view full analytics
                </small>
              </div>
              <div className="card-body p-0">
                {filteredPages.length > 0 ? (
                  <>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '50%' }}>Page</th>
                          <th style={{ width: '35%' }}>Views Distribution</th>
                          <th className="text-end" style={{ width: '15%' }}>Views</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedItems.map((page, index) => (
                          <tr
                            key={index}
                            onClick={() => handlePageClick(page.url)}
                            style={{ cursor: 'pointer' }}
                            className="hover-highlight"
                          >
                            <td>
                              <div>
                                <div className="fw-semibold text-truncate d-flex align-items-center" style={{ maxWidth: '400px' }}>
                                  {page.title || 'Untitled Page'}
                                  <i className="bi bi-arrow-right-circle ms-2 text-muted small"></i>
                                </div>
                                <small className="text-muted text-truncate d-block" style={{ maxWidth: '400px' }}>
                                  {page.url}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div className="progress" style={{ height: '8px' }}>
                                <div
                                  className="progress-bar bg-primary"
                                  role="progressbar"
                                  style={{ width: `${(Number(page.views) / maxViews) * 100}%` }}
                                />
                              </div>
                            </td>
                            <td className="text-end">
                              <span className="badge bg-primary">{formatNumber(Number(page.views))}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top bg-light">
                      <small className="text-muted">
                        Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredPages.length)} of {filteredPages.length}
                      </small>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>‹</button>
                        </li>
                        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                          let page = i + 1;
                          if (totalPages > 7) {
                            if (currentPage <= 4) page = i + 1;
                            else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
                            else page = currentPage - 3 + i;
                          }
                          return (
                            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>›</button>
                        </li>
                      </ul>
                    </div>
                  )}
                  </>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-file-earmark-text fs-1 d-block mb-3"></i>
                    <p className="mb-0">
                      {searchTerm ? 'No pages found matching your search' : 'No page view data available'}
                    </p>
                    {!searchTerm && (
                      <small className="text-muted">Data will appear once visitors start browsing your website</small>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .hover-highlight:hover {
          background-color: rgba(37, 99, 235, 0.05) !important;
        }
        .hover-highlight:hover .bi-arrow-right-circle {
          color: #2563EB !important;
        }
      `}</style>
    </DashboardLayout>
  );
};

export default DetailedAnalyticsPage;
